// Angular
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

// Libraries
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { takeUntil } from 'rxjs';

// Models
import { AppCleanup, UserModel } from '../../../shared/models';

// Store
import { UserAuthActions } from './user-auth.action';

// Environment
import { environment } from '../../../../environments/environment';
import { StorageState } from '../../../shared/state/storage.state';
import { StorageActions } from '../../../shared/state/storage.action';

export interface UserAuthStateModel {
	userLoaded: boolean;
	user: UserModel;
}

const defaultState: UserAuthStateModel = {
	userLoaded: false,
	user: undefined,
};

@State<any>({
	name: 'userAuth',
	defaults: defaultState,
})
@Injectable()
export class UserAuthState extends AppCleanup {
	private returnUrl: string;

	constructor(
		private router: Router,
		private store: Store
	) {
		super();

		this.router.events.pipe(takeUntil(this.destroy)).subscribe((value) => {
			if (value instanceof NavigationEnd) {
				this.returnUrl = value.urlAfterRedirects;
			}
		});
	}

	@Action(UserAuthActions.SetUser)
	setUser(ctx: StateContext<UserAuthStateModel>, action: UserAuthActions.SetUser) {
		ctx.setState({
			userLoaded: true,
			user: action.user,
		});

		this.store.dispatch(new StorageActions.SetTenantId(action.user.tenantId));
	}

	@Action(UserAuthActions.UpdateUser)
	updateUser(ctx: StateContext<UserAuthStateModel>, action: UserAuthActions.UpdateUser) {
		ctx.patchState({
			user: action.user,
		});

		this.store.dispatch(new StorageActions.SetTenantId(action.user.tenantId));
	}

	@Action(UserAuthActions.DeleteUser)
	deleteUser(ctx: StateContext<UserAuthStateModel>) {
		ctx.setState({
			userLoaded: false,
			user: undefined,
		});

		this.store.dispatch(new StorageActions.SetTenantId(undefined));

		this.store.dispatch(new StorageActions.ClearToastHistory()); // Clears notifications

		localStorage.removeItem(environment.authTokenKey);

		this.router.navigate(['/auth/login'], {
			queryParams: { returnUrl: this.returnUrl },
		});
	}

	@Action(UserAuthActions.ConfirmEula)
	confirmEula(ctx: StateContext<UserAuthStateModel>) {
		const state = ctx.getState();
		ctx.setState({
			...state,
			user: {
				...state.user,
				confirmedEula: true,
			},
		});
	}

	@Selector()
	static userLoaded(state: UserAuthStateModel): boolean {
		return state.userLoaded;
	}

	@Selector()
	static currentUser(state: UserAuthStateModel): UserModel {
		return state.user;
	}

	@Selector()
	static currentUserRoleIds(state: UserAuthStateModel): number[] {
		if (!state.user) {
			return [];
		}

		return state.user.roles;
	}
}
